import { useState, useEffect, useRef } from "react";
import { PropertyMappings } from "../PropertyMappingSelector";
import {
	PropertyOption,
	GlobalPropertyMappingSelectorProps,
	GlobalPropertyMappingSelectorViewModel,
} from "./GlobalPropertyMappingSelector.types";

export function useGlobalPropertyMappingSelectorViewModel(
	props: GlobalPropertyMappingSelectorProps,
): GlobalPropertyMappingSelectorViewModel {
	const { onApplyMapping, availableProperties, autoGuessedMappings } = props;

	// Track which fields the user has explicitly modified
	const userSpecifiedFields = useRef<Set<keyof PropertyMappings>>(new Set());

	const [selectedValues, setSelectedValues] = useState<Partial<Record<keyof PropertyMappings, PropertyOption[]>>>({
		grower: [],
		farm: [],
		field: [],
		fieldId: [],
		labName: [],
		labInstructions: [],
		submissionNotes: [],
		yieldGoal: [],
		submitterName: [],
		submitterEmail: [],
		growerPhoneNumber: [],
		eventId: [],
		labSubmissionCode: [],
		externalId: [],
		externalFarmId: [],
		externalLabId: [],
		dateFieldReadyEstimated: [],
		density: [],
		depth: [],
		frequencyOfMicros: [],
		testPackage: [],
		fieldPriority: [],
		nextCrop: [],
		lastCrop: [],
		branch: [],
		salesperson: [],
		salespersonMobilePhone: [],
	});

	// Track if auto-guess notice should be shown
	const [showAutoGuessNotice, setShowAutoGuessNotice] = useState(false);

	// Initialize from auto-guessed values (only for fields user hasn't modified)
	useEffect(() => {
		if (!autoGuessedMappings) {
			return;
		}

		// Only iterate over fields that exist in AggregatedAutoGuessResult (excludes uniqueKey)
		const fields = [
			"grower",
			"farm",
			"field",
			"fieldId",
			"labName",
			"labInstructions",
			"submissionNotes",
			"yieldGoal",
			"submitterName",
			"submitterEmail",
			"growerPhoneNumber",
			"eventId",
			"labSubmissionCode",
			"externalId",
			"externalFarmId",
			"externalLabId",
			"dateFieldReadyEstimated",
			"density",
			"depth",
			"frequencyOfMicros",
			"testPackage",
			"fieldPriority",
			"nextCrop",
			"lastCrop",
			"branch",
			"salesperson",
			"salespersonMobilePhone",
		] as const;
		const newSelectedValues = { ...selectedValues };
		let hasChanges = false;

		for (const field of fields) {
			// Only apply auto-guessed values to fields the user hasn't explicitly modified
			if (!userSpecifiedFields.current.has(field)) {
				const autoGuessedValue = autoGuessedMappings[field];
				if (autoGuessedValue && autoGuessedValue.length > 0) {
					newSelectedValues[field] = autoGuessedValue;
					hasChanges = true;
				}
			}
		}

		if (hasChanges) {
			setSelectedValues(newSelectedValues);
			setShowAutoGuessNotice(true);
		}
		// NOTE: selectedValues intentionally omitted from deps to avoid infinite loop
	}, [autoGuessedMappings]);

	// Reset userSpecifiedFields when all files are cleared (autoGuessedMappings becomes null)
	useEffect(() => {
		if (autoGuessedMappings === null) {
			userSpecifiedFields.current.clear();
			setSelectedValues({
				grower: [],
				farm: [],
				field: [],
				fieldId: [],
				labName: [],
				labInstructions: [],
				submissionNotes: [],
				yieldGoal: [],
				submitterName: [],
				submitterEmail: [],
				growerPhoneNumber: [],
				eventId: [],
				labSubmissionCode: [],
				externalId: [],
				externalFarmId: [],
				externalLabId: [],
				dateFieldReadyEstimated: [],
				density: [],
				depth: [],
				frequencyOfMicros: [],
				testPackage: [],
				fieldPriority: [],
				nextCrop: [],
				lastCrop: [],
				branch: [],
				salesperson: [],
				salespersonMobilePhone: [],
			});
			setShowAutoGuessNotice(false);
		}
	}, [autoGuessedMappings]);

	const handleSelectionChange = (field: keyof PropertyMappings, value: PropertyOption[] | null) => {
		const propertyArray = value || [];

		// Mark this field as user-specified
		userSpecifiedFields.current.add(field);

		// Hide the auto-guess notice when user manually changes any mapping
		setShowAutoGuessNotice(false);

		setSelectedValues((prev) => ({
			...prev,
			[field]: propertyArray,
		}));

		if (propertyArray.length > 0) {
			onApplyMapping(
				field,
				propertyArray.map((v) => v.name),
			);
		} else {
			onApplyMapping(field, null);
		}
	};

	return {
		selectedValues,
		availableProperties,
		shouldRender: availableProperties.length > 0,
		showAutoGuessNotice,
		handleSelectionChange,
	};
}
